import React, { forwardRef } from 'react'
import { NativeSyntheticEvent, TextInputFocusEventData } from 'react-native'
import { twMerge } from 'tailwind-merge'

import {
    Input as GsInput,
    InputField as GsInputField,
    InputIcon as GsInputIcon,
    InputSlot as GsInputSlot,
} from '../../_gluestack/input'
import { FieldFocusProvider, useFieldFocusState, useSetFieldFocused } from '../../fieldFocus'
import { useFieldInvalid } from '../../fieldInvalid'
import { FIELD_TEXT_CLASS } from '../../fieldTypography'

// Prop-driven AOS Input — owns the Figma styling (node 6365:73000) and wraps the
// vendored Gluestack compound (which keeps the interaction wiring:
// data-[hover|invalid|disabled] on the root). The `_gluestack/input` tva is
// neutralized to structure-only, so this file is the single source of truth.
// States: default border/subtle on bg/input → hover border/strong + input-hover
// bg → focus 2px action/focus-ring → invalid 2px status/critical → disabled 40%
// opacity. Figma has a single height (40px / h-10, px-12 py-8); grow via
// `className` if a call site needs it — there is no size axis in the design.
const inputBase = twMerge(
    'flex-row items-center overflow-hidden rounded bg-aos-bg-input h-10 px-3 gap-2',
    'border border-border-subtle',
    'data-[hover=true]:border-border-strong data-[hover=true]:bg-aos-bg-input-hover',
    'data-[invalid=true]:border-2 data-[invalid=true]:border-status-critical',
    // Disabled: not hoverable / clickable. `pointer-events-none` (plain — the
    // `web:` variant isn't generated in the host SB's Tailwind) plus explicit
    // disabled+hover overrides that revert the border/bg so no hover styling
    // leaks through even if the hover data-attr is still set. The 40% dim comes
    // from the `isDisabled` prop below (group-level in a FormControl, field-level
    // standalone) — never both, so no compounding.
    'data-[disabled=true]:border-border-subtle data-[disabled=true]:pointer-events-none',
    'data-[disabled=true]:data-[hover=true]:border-border-subtle data-[disabled=true]:data-[hover=true]:bg-aos-bg-input',
)

type GsInputRef = React.ComponentRef<typeof GsInput>
export type InputProps = Omit<
    React.ComponentPropsWithoutRef<typeof GsInput>,
    'variant' | 'size'
> & {
    className?: string
}

// Figma aliases Semantic/Focus-ring to Action/default, so the frame follows the
// follows the brand (blue on AOS, yellow on AOIMS/TLL).
const inputFocused = 'border-2 border-action-focus-ring data-[hover=true]:border-action-focus-ring'

export const Input = forwardRef<GsInputRef, InputProps>(
    ({ className, isInvalid, ...rest }, ref) => {
        const { isFocused, setFocused } = useFieldFocusState()
        const fieldInvalid = useFieldInvalid()

        return (
            <FieldFocusProvider setFocused={setFocused}>
                <GsInput
                    ref={ref}
                    isInvalid={isInvalid ?? fieldInvalid}
                    className={twMerge(
                        inputBase,
                        isFocused ? inputFocused : '',
                        rest.isDisabled ? 'opacity-40' : '',
                        className,
                    )}
                    {...rest}
                />
            </FieldFocusProvider>
        )
    },
)
Input.displayName = 'Input'

// Figma field text: content/default ink, content/muted placeholder, P2 14.22/21.33.
const inputFieldBase = twMerge(
    'flex-1 min-w-0 h-full px-0 py-0 font-regular',
    FIELD_TEXT_CLASS,
    'text-aos-text-default placeholder:text-aos-text-muted',
)

type GsInputFieldRef = React.ComponentRef<typeof GsInputField>
export type InputFieldProps = React.ComponentPropsWithoutRef<typeof GsInputField> & {
    className?: string
}

export const InputField = forwardRef<GsInputFieldRef, InputFieldProps>(
    ({ className, onFocus, onBlur, ...rest }, ref) => {
        const setFocused = useSetFieldFocused()

        return (
            <GsInputField
                ref={ref}
                className={twMerge(inputFieldBase, className)}
                onFocus={(event: NativeSyntheticEvent<TextInputFocusEventData>) => {
                    setFocused?.(true)
                    onFocus?.(event)
                }}
                onBlur={(event: NativeSyntheticEvent<TextInputFocusEventData>) => {
                    setFocused?.(false)
                    onBlur?.(event)
                }}
                {...rest}
            />
        )
    },
)
InputField.displayName = 'InputField'

type GsInputIconRef = React.ComponentRef<typeof GsInputIcon>
export type InputIconProps = React.ComponentPropsWithoutRef<typeof GsInputIcon> & {
    className?: string
}

export const InputIcon = forwardRef<GsInputIconRef, InputIconProps>(
    ({ className, ...rest }, ref) => (
        <GsInputIcon ref={ref} className={twMerge('text-aos-text-muted', className)} {...rest} />
    ),
)
InputIcon.displayName = 'InputIcon'

export { GsInputSlot as InputSlot }
